import React from "react";
import { FaSearch } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import {
  destinationBrowseOptions,
  type DestinationBrowseFilters,
} from "./destinationBrowseFilters";

type HubHeaderProps = {
  filters: DestinationBrowseFilters;
  onFiltersChange: (filters: DestinationBrowseFilters) => void;
};

const HubHeader: React.FC<HubHeaderProps> = ({ filters, onFiltersChange }) => {
  const { t } = useTranslation();

  const handleSelectChange = <TKey extends keyof DestinationBrowseFilters>(
    key: TKey,
    value: DestinationBrowseFilters[TKey],
  ) => {
    onFiltersChange({
      ...filters,
      [key]: value,
    });
  };

  const handleScrollToResults = () => {
    const target = document.getElementById("destination-collections");
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <section className="relative h-screen overflow-hidden">
      <video
        className="absolute inset-0 z-0 h-screen w-full object-cover"
        autoPlay
        loop
        muted
        playsInline
        preload="metadata"
        aria-hidden="true"
      >
        <source src="/bg.mp4" type="video/mp4" />
      </video>

      <div className="absolute inset-0 z-0 bg-black/45" aria-hidden="true" />

      <div className="relative z-10 mx-auto max-w-7xl px-4 pb-14 pt-28 sm:px-6 md:pb-16 md:pt-44 lg:px-8">
        <div className="mb-12 animate-slide-up text-center">
          <h1 className="mb-4 font-heading text-4xl font-bold leading-tight text-white sm:text-5xl md:text-7xl">
            Discover your <span className="italic font-light">next</span>
            <br />
            {t("destination")}
          </h1>

          <p className="mx-auto max-w-2xl text-lg text-white/85">
            {t("Explore over 400+ curated luxury experiences across the globe.")}
          </p>
        </div>

        <div className="mx-auto max-w-5xl animate-slide-up">
          <div className="relative z-20 mb-0 flex justify-center px-1">
            <div className="inline-flex max-w-full overflow-x-auto rounded-t-xl bg-white shadow-sm">
              {destinationBrowseOptions.tabs.map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() =>
                    onFiltersChange({
                      ...filters,
                      tab,
                    })
                  }
                  className={`whitespace-nowrap px-4 py-3 text-sm font-medium transition-colors sm:px-8 ${
                    filters.tab === tab
                      ? "bg-brand-dark text-white"
                      : "bg-white text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {t(tab)}
                </button>
              ))}
            </div>
          </div>

          <div className="ticket-shape glass-panel relative z-10 flex flex-col items-center gap-4 divide-y divide-gray-200 p-4 shadow-xl md:flex-row md:gap-0 md:divide-x md:divide-y-0 md:p-6">
            <div className="w-full flex-1 px-4 py-2">
              <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500">
                {t("Region")}
              </label>

              <select
                value={filters.region}
                onChange={(event) =>
                  handleSelectChange("region", event.target.value as DestinationBrowseFilters["region"])
                }
                className="w-full bg-transparent text-lg font-medium text-brand-dark focus:outline-none"
              >
                {destinationBrowseOptions.regions.map((region) => (
                  <option key={region} value={region}>
                    {t(region)}
                  </option>
                ))}
              </select>
            </div>

            <div className="w-full flex-1 px-4 py-2">
              <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500">
                {t("Travel Type")}
              </label>

              <select
                value={filters.travelType}
                onChange={(event) =>
                  handleSelectChange(
                    "travelType",
                    event.target.value as DestinationBrowseFilters["travelType"],
                  )
                }
                className="w-full bg-transparent text-lg font-medium text-brand-dark focus:outline-none"
              >
                {destinationBrowseOptions.travelTypes.map((travelType) => (
                  <option key={travelType} value={travelType}>
                    {t(travelType)}
                  </option>
                ))}
              </select>
            </div>

            <div className="w-full flex-1 px-4 py-2">
              <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500">
                {t("Season")}
              </label>

              <select
                value={filters.season}
                onChange={(event) =>
                  handleSelectChange("season", event.target.value as DestinationBrowseFilters["season"])
                }
                className="w-full bg-transparent text-lg font-medium text-brand-dark focus:outline-none"
              >
                {destinationBrowseOptions.seasons.map((season) => (
                  <option key={season} value={season}>
                    {t(season)}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex w-full justify-center px-4 py-2 md:w-auto md:justify-end">
              <button
                type="button"
                onClick={handleScrollToResults}
                className="flex h-14 w-full items-center justify-center rounded-xl bg-yellow-500 text-white shadow-lg shadow-brand-accent/30 transition-colors hover:bg-yellow-600 md:w-16"
              >
                <FaSearch className="text-xl" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HubHeader;
